import { useState, useEffect, useRef } from 'react';

const PREFIX = 'ua_view_state:';
let isClearing = false;

/**
 * useState that survives navigation and page reload. Cleared on logout via
 * clearPersistedViewState(). Scope keys per page (and per channel where needed):
 *   usePersistedState(`client-tracker:${ibId}:search`, '')
 */
export function usePersistedState(key, initialValue) {
  const storageKey = PREFIX + key;
  const [value, setValue] = useState(() => {
    try {
      const raw = localStorage.getItem(storageKey);
      if (raw === null) return initialValue;
      return JSON.parse(raw);
    } catch {
      return initialValue;
    }
  });

  const keyRef = useRef(storageKey);
  useEffect(() => {
    // Key changed (e.g. switched channel) — load that scope's value.
    if (keyRef.current !== storageKey) {
      keyRef.current = storageKey;
      try {
        const raw = localStorage.getItem(storageKey);
        setValue(raw === null ? initialValue : JSON.parse(raw));
      } catch {
        setValue(initialValue);
      }
      return;
    }
    if (isClearing) return;
    try {
      localStorage.setItem(storageKey, JSON.stringify(value));
    } catch {}
  }, [storageKey, value]);  // isClearing is intentionally not a dependency

  return [value, setValue];
}

/**
 * Keeps the last known non-empty id so the storage key stays stable while the
 * parent page is re-fetching the channel (ib prop briefly becomes undefined).
 */
export function useStableKeyId(id) {
  const ref = useRef(id);
  if (id) ref.current = id;
  return ref.current || 'none';
}

export function clearPersistedViewState() {
  isClearing = true;
  try {
    Object.keys(localStorage)
      .filter((k) => k.startsWith(PREFIX))
      .forEach((k) => localStorage.removeItem(k));
  } catch {}
}
